import { BaseNavComponent, LayerNavComponent } from './baseClasses';
import { navigationLayers } from '../layers/admin';
import { sharedPointSources } from '../layers/helpers';
import { getStateFeatures, getDistrictFeatures, getMunicipalityFeatures } from '../api';
import { fitToExtent } from '../mapHelpers';

const promisifiedTimeout = (time) => new Promise(resolve => setTimeout(resolve, time));

export class Country extends BaseNavComponent
{
    constructor({ innerComponent, highlightSource })
    {
        super({ innerComponent, highlightSource });
    }


    async select(code, {reselect=false}={})
    {
        console.assert([0, '0', '00', '000', -1].includes(code), `Invalid code ${code}`)
        if (code===-1) return;
        if (!reselect && this.currentCode===code) return;
        this.currentCode = code;
        this.beforeSelect(code);
        
        // we can't clear from state to municipality. If we do that, we won't have any feature 
        // to execute click event. In short the invisible state layer always stays.
        const districtComponent = this.innerComponent.innerComponent;
        districtComponent.clearFeatures({recursive: true});
        this.innerComponent.currentCode = -1;
        this.highlightSource.clear(); // clearing the highlight layer source features

        if (code===0 || code==='0') // all state in country
        {
            // since state features are never removed & already there
            this.innerComponent.currentCode = 0;
        }
        else if (code==='00')// all districts in country
        {
            districtComponent.addFeatures(await districtComponent.getAllFeaturesFromCache());
            districtComponent.currentCode = 0; // all districts code for district component
        }
        else if (code==='000') // all municipalities in country
        {
            const municipalityComponent = districtComponent.innerComponent;
            municipalityComponent.addFeatures(await municipalityComponent.getAllFeaturesFromCache());
            municipalityComponent.currentCode = 0; // all municipalities code for municipality component
        }

        fitToExtent(this.innerComponent.source.getExtent()); // using state's source because extent is always same (all states)

        await this.afterSelect(code);
    }
}

export class State extends LayerNavComponent
{
    constructor({innerComponent, highlightSource})
    {
        super({
            layer: navigationLayers.state,
            codeField: 'SCode',
            labelField: 'Province',
            nepaliLabelField: "Nepali",
            innerComponent,
            highlightSource,
            sharedPointSources,
            linkedSources: [
                sharedPointSources.state,
            ],
        });
        this.pluralName = "States";
        this.pluralNameNepali = 'Pradesh'; 
        // adding state to the layer right away
        this.getAllFeaturesFromCache().then( features => this.addFeatures(features) );
    }

    getParentCode()
    {
        return 0; // because for national code, 0 => select all state 
    }

    async select(code, {reselect=false}={})
    {
        if (code==='0000') // all municipalities in current state
        {
            this.beforeSelect(code);

            this.highlightSource.clear();
            const district = this.innerComponent;
            const municipality = this.innerComponent.innerComponent;
            district.clearFeatures({recursive: true});
            const allMuniInCurrState = (await municipality.getAllFeaturesFromCache()).filter(ftr=>ftr.get(this.codeField)===this.currentCode);
            municipality.addFeatures(allMuniInCurrState);
            municipality.currentCode=0;

            this.updateHighlightSource(this.currentCode);
            fitToExtent(this.highlightSource.getExtent());

            await this.afterSelect(code);
        }
        else
        {
            await super.select(code, {reselect});
        }
    }

    async getAllFeaturesFromCache()
    {
        return await getStateFeatures();
    }
}

export class District extends LayerNavComponent
{
    constructor({innerComponent, highlightSource})
    {
        super({
            layer: navigationLayers.district,
            codeField: 'DCode',
            labelField: 'District',
            nepaliLabelField: 'Nepali',
            innerComponent,
            highlightSource,
            linkedSources: [
                sharedPointSources.district,
            ],
        });
        this.pluralName = "Districts";
        this.pluralNameNepali = "जिल्लाहरु";
    }

    async getAllFeaturesFromCache()
    {
        return await getDistrictFeatures();
    }

    async select(code, {reselect=false}={})
    {
        const state = this.parentComponent;
        if ([0,-1].includes(state.currentCode) && code===0) // if all state is selected && all district selected
        {
            const country = state.parentComponent;
            await country.select('00', {reselect: true}); // all districts in country
        }
        else if ([0,-1].includes(state.currentCode) & code!==0) // if a district is selected & corresponding state is not
        {
            const scode = (await this.getAllFeaturesFromCache()).find(ftr => ftr.get('DCode')===code).get('SCode');
            await state.select(scode);
            await promisifiedTimeout(1200);
            await this.select(code);
        }
        else
        {
            if(code!==0 && this.source.getFeatures().length===0)
            {
                const features = (await this.getAllFeaturesFromCache()).filter(ftr => ftr.get(state.codeField)===state.currentCode);
                this.addFeatures(features);
            }
            await super.select(code, {reselect});
        }
    }

    async getSelectableFeatures()
    {
        let districts = this.source.getFeatures();
        const state = this.parentComponent;
        if ([0, -1].includes(state.currentCode)) // if all state is selected
        {
            districts = await getDistrictFeatures();
        }
        // if a state is selected, district is not selected & all muni is selected
        // then district source will be empty so we need to add features
        else if (this.currentCode===-1 && this.innerComponent.currentCode===0)
        {
            districts = (await getDistrictFeatures()).filter(ftr => ftr.get(state.codeField)===state.currentCode);
        }
        return districts;
    }
}

export class Municipality extends LayerNavComponent
{
    constructor({innerComponent, highlightSource})
    {
        super({
            layer: navigationLayers.municipality,
            codeField: 'SDDMM',
            labelField: 'GaPa_NaPa',
            nepaliLabelField: 'Nepali',
            innerComponent,
            highlightSource,
            linkedSources: [
                sharedPointSources.municipality,
            ],
        });
        this.pluralName = "Municipalities";
        this.pluralNameNepali = "पालिकाहरु";
    }

    async getAllFeaturesFromCache()
    {
        return await getMunicipalityFeatures();
    }

    async select(code, {reselect=false}={})
    {
        const district = this.parentComponent;
        const state = this.parentComponent.parentComponent;
        if ([0,-1].includes(state.currentCode) && code===0) // if no state is selected && all municipalities selected
        {
            const country = state.parentComponent;
            await country.select('000', {reselect: true}); // select all muni in country
        }
        else if (![0,-1].includes(state.currentCode) && [0,-1].includes(district.currentCode) && code===0) // all muni in curr state
        {
            await state.select('0000');
        }
        else if ([0,-1].includes(district.currentCode) && code!==0)// if municipality is selected & corresponding district has not selected
        {
            const dcode = (await this.getAllFeaturesFromCache()).find(ftr => ftr.get(this.codeField)===code).get(district.codeField);
            await district.select(dcode);
            await promisifiedTimeout(1200);
            await this.select(code);
        }
        else
        {
            await super.select(code, {reselect});
        }
    }

    async getSelectableFeatures()
    {
        let municipalities = this.source.getFeatures();
        const district = this.parentComponent;
        const state = this.parentComponent.parentComponent;
        if ([-1,0].includes(district.currentCode))
        {
            const allMunicipalities = await getMunicipalityFeatures();
            municipalities = ([-1,0].includes(state.currentCode)) ?
                allMunicipalities
                :
                allMunicipalities.filter(f => f.get('SCode')===state.currentCode);
        }
        return municipalities;
    }
}

export class SoloMunicipality extends LayerNavComponent
{
    constructor({highlightSource})
    {
        super({
            layer: navigationLayers.soloMunicipality,
            codeField: 'SDDMM',
            labelField: 'GaPa_NaPa',
            nepaliLabelField: 'Nepali',
            highlightSource: highlightSource,
        });
    }
    
    async getAllFeaturesFromCache()
    {
        return await getMunicipalityFeatures();
    }
}